import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Trash2, Calendar, Search, BarChart3, Quote, Reply, Send, X, ShieldCheck } from "lucide-react";

const AdminReviews = () => {
  const [allReviews, setAllReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [replyingTo, setReplyingTo] = useState(null); 
  const [replyText, setReplyText] = useState("");

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const savedUser = localStorage.getItem("weThriftUser");
      const token = savedUser ? JSON.parse(savedUser)?.accessToken : null;

      const res = await axios.get("https://wethrift-backend.onrender.com/api/product/admin/all-reviews", {
        headers: { token: `Bearer ${token}` }
      });
      setAllReviews(res.data);
      setFilteredReviews(res.data);
    } catch (err) {
      console.log("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, []);

  // Search Logic
  useEffect(() => {
    let temp = allReviews.filter(rev => 
      rev.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (rev.username || "Guest").toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredReviews(temp);
  }, [searchTerm, allReviews]);

const handleReplySubmit = async (productId, reviewId) => {
  try {
    const savedUser = localStorage.getItem("weThriftUser");
    const token = JSON.parse(savedUser)?.accessToken;

    // Yahan check karo ki object mein "replyText" hi likha hai
    const res = await axios.put(
      `http:///api/product/review-reply/${productId}/${reviewId}`,
      { replyText: replyText }, // 👈 Is naam ko backend ke req.body.replyText se match hona chahiye
      { headers: { token: `Bearer ${token}` } }
    );

    if (res.status === 200) {
      alert("Reply posted!");
      setReplyText("");
      setReplyingTo(null);
      await fetchReviews(); // Data refresh karne ke liye
    }
  } catch (err) {
    console.error(err);
  }
};

  const handleDelete = async (productId, reviewId) => {
    if (window.confirm("Purge this record from database?")) {
      try {
        const savedUser = localStorage.getItem("weThriftUser");
        const token = JSON.parse(savedUser)?.accessToken;
        await axios.delete(`http:///api/product/review/${productId}/${reviewId}`, {
          headers: { token: `Bearer ${token}` }
        });
        fetchReviews();
      } catch (err) {
        alert("Action restricted.");
      }
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-emerald-500 font-black tracking-[0.5em] animate-pulse italic">SYNCING_VERDICTS...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-300 p-4 lg:p-10 font-sans">
      
      {/* HEADER SECTION */}
     {/* --- VERDICT_STATION HEADER (RESPONSIVE) --- */}
<div className="max-w-7xl mx-auto mb-6 lg:mb-10 px-4 lg:px-0">
  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 border-b border-zinc-900 pb-6 lg:pb-8">
    
    <div className="w-full lg:w-auto">
      {/* Responsive Heading */}
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black italic text-white uppercase tracking-tighter leading-none">
        VERDICT<span className="text-emerald-500">_STATION</span>
      </h1>
      
      {/* Stats Badge */}
      <div className="flex gap-3 mt-4 lg:mt-5">
        <div className="bg-zinc-900/50 backdrop-blur-sm px-4 py-2 rounded-xl border border-zinc-800 flex items-center gap-2">
          <BarChart3 size={14} className="text-emerald-500" />
          <span className="text-[9px] lg:text-[11px] font-black uppercase tracking-widest text-zinc-400">
            {allReviews.length} TOTAL_FEEDBACKS
          </span>
        </div>
      </div>
    </div>

    {/* Search Box - Full width on mobile */}
    <div className="relative w-full lg:w-80 group">
      <Search 
        className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 group-focus-within:text-emerald-500 transition-colors" 
        size={18}
      />
      <input 
        type="text" 
        placeholder="Filter by Product or User..." 
        className="w-full bg-zinc-950/50 border border-zinc-800 rounded-xl lg:rounded-2xl py-3.5 lg:py-4 pl-12 pr-4 text-[11px] lg:text-sm focus:border-emerald-500/50 outline-none transition-all text-white placeholder:text-zinc-600 shadow-xl"
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      
      {/* Glow Effect for Search (Hidden on mobile for performance) */}
      <div className="hidden lg:block absolute inset-0 -z-10 bg-emerald-500/5 blur-xl opacity-0 group-focus-within:opacity-100 transition-opacity" />
    </div>

  </div>
</div>

      {/* REVIEWS LIST */}
      <div className="max-w-7xl mx-auto space-y-8">
        {filteredReviews.map((rev) => (
          <div key={rev._id} className="group bg-[#0a0a0a] border border-zinc-900 rounded-[3rem] p-6 lg:p-10 hover:border-zinc-700 transition-all duration-500 relative overflow-hidden">
            
            <div className="flex flex-col lg:flex-row gap-10 relative z-10">
              
              {/* PRODUCT INFO PANEL */}
              <div className="lg:w-1/4 shrink-0 flex flex-col items-center lg:items-start border-b lg:border-b-0 lg:border-r border-zinc-900 pb-6 lg:pb-0 lg:pr-10">
                <div className="relative group/img">
                  <img src={rev.productImg} alt="" crossOrigin="anonymous" className="w-24 h-32 object-cover rounded-3xl border-2 border-zinc-800 group-hover/img:border-emerald-500 transition-all duration-500 shadow-2xl" />
                  <div className={`absolute -top-3 -right-3 px-3 py-1.5 rounded-2xl font-black text-xs shadow-xl ${rev.rating >= 4 ? 'bg-emerald-500 text-black' : 'bg-red-500 text-white'}`}>
                    {rev.rating}.0 ★
                  </div>
                </div>
                <div className="mt-6 text-center lg:text-left">
                  <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-1">{rev.productCategory}</p>
                  <h3 className="text-white font-black text-sm uppercase leading-tight mb-2">{rev.productName}</h3>
                  <div className="inline-block px-3 py-1 bg-zinc-900 rounded-lg text-[9px] text-zinc-500 font-mono tracking-tighter">REF_{rev.productId.slice(-8).toUpperCase()}</div>
                </div>
              </div>

              {/* REVIEW & REPLY CENTER */}
              <div className="flex-1">
                {/* User Info Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-zinc-800 to-zinc-950 border border-zinc-800 flex items-center justify-center text-emerald-500 font-black text-lg shadow-lg">
                      {(rev.username || "G").charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <h4 className="text-white font-black text-xs uppercase tracking-widest">{rev.username || "Guest"}</h4>
                        <div className="flex items-center gap-2 text-zinc-500 text-[10px] font-bold mt-0.5">
                            <Calendar size={12} className="text-zinc-700"/> {rev.date}
                        </div>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(rev.productId, rev._id)} className="p-3 text-zinc-700 hover:text-red-500 transition-colors">
                    <Trash2 size={20}/>
                  </button>
                </div>

                {/* THE COMMENT CARD */}
                <div className="bg-[#111] border border-zinc-800 rounded-[2.5rem] p-8 relative overflow-hidden group-hover:border-zinc-700 transition-all">
                  <Quote className="absolute -top-2 -right-2 text-zinc-900 w-24 h-24 rotate-12 opacity-40" />
                  
                  <p className="text-zinc-100 text-lg lg:text-xl font-medium leading-relaxed italic relative z-10 pr-10">
                    "{rev.comment}"
                  </p>

                  {/* ADMIN REPLY DISPLAY (IF EXISTS) */}
                  {rev.adminReply && (
                    <div className="mt-8 pt-8 border-t border-zinc-800/50 flex gap-4 animate-in fade-in slide-in-from-left-4">
                      <div className="shrink-0 pt-1">
                        <ShieldCheck className="text-emerald-500" size={20}/>
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.2em] mb-1">Official Support Team</p>
                        <p className="text-zinc-400 text-sm italic leading-relaxed font-light select-all">
                          {rev.adminReply}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* REPLY INPUT AREA */}
                  {replyingTo === rev._id ? (
                    <div className="mt-8 flex gap-3 animate-in zoom-in-95 duration-200">
                      <input 
                        className="flex-1 bg-black border border-emerald-500/50 rounded-2xl px-5 py-4 text-sm text-white outline-none focus:ring-2 ring-emerald-500/20 shadow-2xl"
                        placeholder="Respond to this feedback..."
                        autoFocus
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                      />
                      <button onClick={() => handleReplySubmit(rev.productId, rev._id)} className="px-6 bg-emerald-500 text-black rounded-2xl font-black uppercase text-[10px] flex items-center gap-2 hover:bg-emerald-400 transition-all">
                        <Send size={16}/> POST
                      </button>
                      <button onClick={() => setReplyingTo(null)} className="p-4 bg-zinc-800 text-zinc-400 rounded-2xl hover:bg-zinc-700 transition-all">
                        <X size={20}/>
                      </button>
                    </div>
                  ) : (
                    <div className="mt-8">
                      <button 
                        onClick={() => { setReplyingTo(rev._id); setReplyText(rev.adminReply || ""); }}
                        className="group/reply flex items-center gap-3 px-6 py-3 bg-zinc-900 border border-zinc-800 rounded-2xl text-[10px] font-black text-zinc-400 uppercase tracking-widest hover:border-emerald-500/50 hover:text-white transition-all shadow-lg"
                      >
                        <Reply size={14} className="group-hover/reply:-rotate-12 transition-transform"/> 
                        {rev.adminReply ? "Edit Response" : "Public Response"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Background Accent Gradient */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-[120px] -z-10 group-hover:bg-emerald-500/10 transition-all" />
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminReviews;